import { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import Button from "../components/Button";
import Card from "../components/Card";
import Input from "../components/Input";
import { Stack } from "../components/Layout";
import PageShell from "../components/PageShell";
import { Heading, Text } from "../components/Typography";
import { apiPost, ApiError } from "../services/apiClient";
import { setSession } from "../services/authStorage";

const FormCard = styled(Card)`
  width: 100%;
`;

const ErrorText = styled(Text)`
  color: ${({ theme }) => theme.colors.danger};
  font-size: ${({ theme }) => theme.typography.sizes.sm};
`;

function CreateUser() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");

    if (!username.trim() || !password) {
      setError("Username and password are required.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const data = await apiPost(
        "/user_creation",
        { name: username, password },
        { skipAuth: true }
      );

      const apiKey = Array.isArray(data) ? data[0] : data?.apiKey;
      const message = Array.isArray(data)
        ? data[1]
        : data?.message || "Account created.";

      if (!apiKey) {
        throw new ApiError("Account created but no API key was returned.");
      }

      setSession({ username, apiKey });
      alert(
        `${message}\n\nYour API key:\n${apiKey}\n\nSave this key — it will not be shown again.`
      );
      navigate("/SignIn");
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : "Account creation failed. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <PageShell narrow>
      <FormCard padding={4}>
        <form onSubmit={handleSubmit}>
          <Stack $gap={4}>
            <Stack $gap={1}>
              <Heading $size="xl">Create user</Heading>
              <Text $muted $size="sm">
                Register for an account to use the dashboard
              </Text>
            </Stack>
            <Stack $gap={3}>
              <Input
                label="Username"
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <Input
                label="Password"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Input
                label="Confirm password"
                type="password"
                placeholder="Re-enter password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <Input
                label="Email"
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {error && <ErrorText>{error}</ErrorText>}
              <Button type="submit" disabled={loading}>
                {loading ? "Creating account..." : "Submit"}
              </Button>
            </Stack>
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate("/SignIn")}
            >
              Already have an account? Sign in
            </Button>
          </Stack>
        </form>
      </FormCard>
    </PageShell>
  );
}

export default CreateUser;
